import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterToolbar from "@/components/feature/FilterToolbar";
import VideoGrid from "@/components/feature/VideoGrid";
import Pagination from "@/components/feature/Pagination";
import { categoryMap } from "@/mocks/categories";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import type { SortValue } from "@/types/video";
import { filterVideos, getVideosByType, paginate } from "@/utils/video";

export default function Category() {
  const { type = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const category = categoryMap[type];

  const region = searchParams.get("region") || "全部";
  const year = searchParams.get("year") || "all";
  const sort = (searchParams.get("sort") as SortValue) || "latest";
  const page = Number(searchParams.get("page")) || 1;

  useDocumentMeta(
    category ? `${category.name} - 污视频视频资源` : "分类 - 污视频",
    category
      ? `这里汇聚了丰富的${category.name}视频内容，包含最新和热门作品，满足您的观看需求。`
      : undefined,
  );

  const result = useMemo(() => {
    if (!category) {
      return { pageItems: [], total: 0, totalPages: 1, page: 1 };
    }
    const filtered = filterVideos(getVideosByType(category.type), {
      region,
      year,
      sort,
    });
    return paginate(filtered, page);
  }, [category, region, year, sort, page]);

  const updateParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === "") {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    setSearchParams(next);
  };

  if (!category) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-error-warning-line text-4xl"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
            未找到该分类
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            你访问的分类不存在，返回首页继续浏览吧。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const resetFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  return (
    <Layout>
      <div className="w-full px-4 pb-6 pt-6 md:px-8 md:pt-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "分类", to: `/category/${category.type}` },
            { label: category.name },
          ]}
        />
        <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className={`${category.icon} text-2xl`}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {category.name}
              </h1>
              <p className="mt-1 text-sm text-foreground-500 md:text-base">
                {category.tagline} · {category.description}
              </p>
            </div>
          </div>
          <span className="shrink-0 rounded-full bg-secondary-100 px-4 py-1.5 text-sm font-medium text-secondary-800">
            共 {result.total} 部资源
          </span>
        </div>
      </div>

      <div className="w-full px-4 md:px-8">
        <FilterToolbar
          region={region}
          year={year}
          sort={sort}
          onRegionChange={(value) => updateParams({ region: value, page: null })}
          onYearChange={(value) => updateParams({ year: value, page: null })}
          onSortChange={(value) => updateParams({ sort: value, page: null })}
          onReset={resetFilters}
        />
      </div>

      <div className="w-full px-4 py-6 md:px-8 md:py-8">
        <VideoGrid videos={result.pageItems} onReset={resetFilters} />
        <Pagination
          page={result.page}
          totalPages={result.totalPages}
          onChange={(nextPage) => {
            updateParams({ page: nextPage === 1 ? null : String(nextPage) });
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      </div>
    </Layout>
  );
}